import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ButtonTypeEnum} from '@shared/button/button.directive';
import {TranslateService} from '@ngx-translate/core';
import {HttpErrorResponseInterface} from '@core/interfaces/http-error-response.interface';
import {ActionsSubject, Store} from '@ngrx/store';
import {Subscription} from 'rxjs';
import {GroupActions, GroupActionsEnum, GroupSelectors} from '@store/group';
import {ofType} from '@ngrx/effects';
import {selectCurrentUser} from '@store/security';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RoleEnum} from '@core/security/enum/role.enum';
import {AuthorizationService} from '@core/security/services/authorization.service';
import {UserAuthenticatedInterface} from '@shared/interfaces/user.interface';
import {GroupInterface} from '@shared/interfaces/group.interface';
import {LibrarySelectors} from '@store/library';
import {take} from 'rxjs/operators';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';

enum GroupsFormFields {
    createGroup = 'createGroup',
}

export interface GroupsDialogDataInterface {
    bookUuid: string;
}

@Component({
    selector: 'app-groups-dialog',
    templateUrl: './groups-dialog.component.html',
    styleUrls: ['./groups-dialog.component.scss'],
})
export class GroupsDialogComponent implements OnDestroy, OnInit {
    @ViewChild('groupCode')
    groupCode?: ElementRef;

    readonly buttonType = ButtonTypeEnum;

    readonly groupsFormFields = GroupsFormFields;

    readonly isRoleVo: boolean;

    readonly roleEnum = RoleEnum;

    groupForm: FormGroup;

    user?: UserAuthenticatedInterface;

    bookUuid?: string;

    groups: GroupInterface[] = [];

    groupsVisible: GroupInterface[] = [];

    groupSelected?: GroupInterface;

    groupsSelected: string[] = [];

    isLoading: boolean = false;

    subscriptions: Subscription[] = [];

    isMobile: boolean = false;

    constructor(
        private dialogRef: MatDialogRef<GroupsDialogComponent>,
        private translateService: TranslateService,
        private formBuilder: FormBuilder,
        private store: Store,
        private actionsSubject: ActionsSubject,
        private matSnackBar: MatSnackBar,
        private authorizationService: AuthorizationService,
        private breakpointObserver: BreakpointObserver,
        @Inject(MAT_DIALOG_DATA) public dialogData: GroupsDialogDataInterface,
    ) {
        this.subscriptions.push(
            actionsSubject.pipe(ofType(GroupActionsEnum.CreateGroupsSuccess)).subscribe(() => this.#handleCreateGroupSuccess()),
            actionsSubject.pipe(ofType(GroupActionsEnum.CreateGroupsFailed)).subscribe(error => this.#handleHttpErrorResponse(error)),
            actionsSubject.pipe(ofType(GroupActionsEnum.JoinGroupsSuccess)).subscribe(() => this.#handleJoinGroupSuccess()),
            actionsSubject.pipe(ofType(GroupActionsEnum.JoinGroupsFailed)).subscribe(error => this.#handleHttpErrorResponse(error)),
            store.select(GroupSelectors.selectOrganizationGroups).subscribe(groups => this.handleGroups(groups)),
            store.select(selectCurrentUser).subscribe(user => this.handleCurrentUser(user)),
        );
        this.groupForm = this.formBuilder.group({
            [GroupsFormFields.createGroup]: new FormControl(null, Validators.required),
        }, {
            updateOn: 'submit',
        });

        if (dialogData) {
            this.bookUuid = this.dialogData.bookUuid;
        }

        this.isMobile = this.breakpointObserver.isMatched(Breakpoints.XSmall);
        this.isRoleVo = this.authorizationService.isGranted(RoleEnum.RoleOrganizationVo);
    }

    ngOnInit(): void {
        this.#fetchOrganizationGroups();
    }

    #fetchOrganizationGroups(): void {
        if (this.authorizationService.isGranted(RoleEnum.RoleTeacher)) {
            this.store.dispatch(GroupActions.fetchOrganizationGroups({bookUuid: this.bookUuid}));
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    onSubmit(): void {
        if (this.authorizationService.isGranted(RoleEnum.RoleStudent)) {
            this.joinGroupStudent();

            return;
        }

        if (this.groupsSelected.length) {
            this.joinGroupTeacher();

            return;
        }

        if (!this.groupForm.valid || this.groupForm.get('createGroup')?.value.length <= 1) {
            this.#setErrorMessage('groups.dialog.validation.notValid');

            return;
        }

        this.isLoading = true;
        this.store.dispatch(GroupActions.createGroup({groupName: this.groupForm.get('createGroup')?.value, bookUuid: this.bookUuid}));
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    handleCheckedEvent(checkedGroup: GroupInterface, isChecked: boolean, event: Event): void {
        event.preventDefault();
        this.groupSelected = checkedGroup;

        if (!isChecked) {
            if (this.groupsSelected.find(code => checkedGroup.code === code) !== undefined) {
                this.groupsSelected.splice(this.groupsSelected.indexOf(checkedGroup.code), 1);
            }
        } else {
            if (this.groupsSelected.find(code => checkedGroup.code === code) === undefined) {
                this.groupsSelected.push(checkedGroup.code);
            }
        }

        this.groupsVisible.sort((a, b) => this.groupsSelected.indexOf(b.code) - this.groupsSelected.indexOf(a.code));
    }

    handleInput(inputValue: string): void {
        if (!inputValue.length) {
            this.groupsVisible = [];

            return;
        }

        this.#filterGroups(inputValue);
    }

    #filterGroups(groupName: string): void {
        this.groupsVisible = this.groups.filter((group) => {
            return new RegExp(`${groupName}`, 'i').exec(group.name);
        });
    }

    private joinGroupStudent(): void {
        if (!this.groupForm.valid || this.groupForm.get('createGroup')?.value.length < 9) {
            this.#setErrorMessage('groups.dialog.validation.notValidCodeStudent');

            return;
        }

        this.isLoading = true;

        this.store.dispatch(GroupActions.joinGroups({codes: [this.groupForm.get('createGroup')?.value]}));
    }

    private joinGroupTeacher(): void {
        this.isLoading = true;

        this.store.select(LibrarySelectors.selectBook)
            .pipe(take(1))
            .subscribe(book => this.store.dispatch(GroupActions.joinGroups({codes: this.groupsSelected, book})));
    }

    private handleCurrentUser(user?: UserAuthenticatedInterface): void {
        if (undefined === user) {
            return;
        }

        this.user = user;
    }

    private handleGroups(groups?: GroupInterface[] | undefined): void {
        if (!groups) {
            return;
        }

        this.groups = groups;
    }

    #handleCreateGroupSuccess(): void {
        this.store.dispatch(GroupActions.fetchGroups({bookUuid: this.bookUuid}));

        this.#fetchOrganizationGroups();

        this.#successMessage();
        this.dialogRef.close();
    }

    #handleJoinGroupSuccess(): void {
        this.store.dispatch(GroupActions.fetchGroups({bookUuid: this.bookUuid}));

        this.#fetchOrganizationGroups();

        this.#successMessageExistingClass();
        this.dialogRef.close();
    }

    #handleHttpErrorResponse(apiErrorResponse?: HttpErrorResponseInterface): void {
        if (undefined === apiErrorResponse) {
            return;
        }

        if (this.authorizationService.isGranted(RoleEnum.RoleTeacher)) {
            this.#setErrorMessage('groups.dialog.validation.serverError');
        } else {
            this.#setErrorMessage('groups.dialog.validation.notValidCodeStudent');
        }

        this.isLoading = false;
    }

    #setErrorMessage(error: string): void {
        const translatedError: string = this.translateService.instant(error);

        this.groupForm.get(GroupsFormFields.createGroup)?.setErrors([translatedError]);
    }

    #successMessage(): void {
        this.matSnackBar.open(this.translateService.instant('groups.dialog.validation.serverSuccess'), 'success', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
        });
    }

    #successMessageExistingClass(): void {
        this.matSnackBar.open(this.translateService.instant('groups.dialog.validation.serverSuccessExisting'), 'success', {
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
        });
    }
}
